import { defineComponent as _defineComponent } from 'vue'
import { TUIConferenceStatus } from '@tencentcloud/tuiroom-engine-js';
import {
  ref,
  nextTick,
  computed,
  onMounted,
} from 'vue';
import { roomService } from '../../services';
import useRoomInfo from '../RoomHeader/RoomInfo/useRoomInfoHooks';
import SvgIcon from '../common/base/SvgIcon.vue';
import Dialog from '../common/base/Dialog';
import EllipsisIcon from '../common/icons/EllipsisIcon.vue';
import ChevronRight from '../common/icons/ChevronRight.vue';
import ArrowDown from '../common/icons/ArrowDown.vue';
import LinkIcon from '../common/icons/LinkIcon.vue';
import SuccessIcon from '../common/icons/SuccessIcon.vue';
import WarningIcon from '../common/icons/WarningIcon.vue';
import TuiButton from '../common/base/Button.vue';
import { useI18n } from '../../locales';
import '../../directives/vClickOutside';
import ScheduleConferencePanel from './ScheduleConferencePanel';
import { getUrlWithRoomId, convertSecondsToHMS } from '../../utils/utils';
import { useBasicStore } from '../../stores/basic';
import { storeToRefs } from 'pinia';
import { isMobile } from '../../utils/environment';
import ConferenceDetail from './ConferenceDetail.vue';
import InvitePanel from './InvitePanel.vue';
import PanelContainer from './PanelContainer.vue';
import logger from '../../utils/common/logger';

type StatusMapType = {
  [key: number]: {
    text: string;
    className: string;
  };
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ScheduleRoomControl',
  props: {
    item: null,
    scheduleStartDate: null,
    scheduleEndDate: null,
    scheduleStartTime: null,
    scheduleEndTime: null
  },
  emits: ['join-conference', 'show-more'],
  setup(__props: any, { emit }) {

const props = __props as {
  item: any;
  scheduleStartDate: string;
  scheduleEndDate: string;
  scheduleStartTime: string;
  scheduleEndTime: string;
};

const basicStore = useBasicStore();
const { userId, userName, isRoomLinkVisible } = storeToRefs(basicStore);

const roomLinkConfig = roomService.getComponentConfig('RoomLink');

const { t } = useI18n();
const showMoreControl = ref(false);
const dropdownClass = ref('down');
const moreBtnRef = ref();
const operateListRef = ref();
const modifyDialogVisible = ref(false);

const showRoomDetail = ref(false);
const showRoomCancel = ref(false);
const showRoomInvite = ref(false);
const roomInfo = ref();



const { onCopy } = useRoomInfo();

const statusMap: StatusMapType = {
  [TUIConferenceStatus.kConferenceStatusRunning]: {
    text: 'Ongoing',
    className: 'status-running',
  },
};

const getStatusTextAndClass = (status: TUIConferenceStatus) =>
  statusMap[status] || { text: '', className: '' };

const isOwner = computed(
  () =>
    props.item.basicRoomInfo.ownerId === roomService.basicStore.userId &&
    props.item.status === TUIConferenceStatus.kConferenceStatusNotStarted
);

const isShowPassword = computed(() => !!roomInfo.value?.password);

const viewDetails = computed(() => ({
  key: 'roomDetails',
  title: t('view details'),
  visible: true,
  func: () => {
    showRoomDetail.value = true;
    showMoreControl.value = false;
  },
}));

const modifyRoom = computed(() => ({
  key: 'modifyRoom',
  title: t('modify room'),
  visible: isOwner.value,
  func: () => {
    modifyDialogVisible.value = true;
    showMoreControl.value = false;
  },
}));

const cancelRoom = computed(() => ({
  key: 'cancelRoom',
  title: t('cancel room'),
  visible: isOwner.value,
  func: () => {
    showRoomCancel.value = true;
    showMoreControl.value = false;
  },
}));

const moreControlList = computed(() => [
  viewDetails.value,
  modifyRoom.value,
  cancelRoom.value,
]);
const scheduleAttendeesInfo = computed(() =>
  props.item.scheduleAttendees
    ? props.item.scheduleAttendees
        .map(
          (attendee: { userId: string; userName: string }) =>
            attendee.userName || attendee.userId
        )
        .join('; ')
    : ''
);
const durationTime = computed(
  () => props.item.scheduleEndTime - props.item.scheduleStartTime
);

function getScheduleTime(timestamp: number) {
  const date = new Date(timestamp);
  const hours = `${date.getHours() < 10 ? `0${date.getHours()}` : date.getHours()}`;
  const minutes = `${date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes()}`;
  return `${hours}:${minutes}`;
}

const scheduleRoomDetailList = computed(() => {
  const { hours, minutes } = convertSecondsToHMS(
    props.item.scheduleEndTime - props.item.scheduleStartTime
  );
  return [
    {
      title: 'Room Name',
      content: props.item.basicRoomInfo.roomName,
      isShowCopyIcon: false,
      status: null,
      isShowStatus: false,
      isVisible: true,
    },
    {
      title: 'Room ID',
      content: props.item.basicRoomInfo.roomId,
      isShowCopyIcon: true,
      status: null,
      isShowStatus: false,
      isVisible: true,
    },
    {
      title: 'Room Time',
      content: `${props.scheduleStartDate} ${props.scheduleStartTime} - ${props.scheduleEndDate} ${props.scheduleEndTime}`,
      isShowCopyIcon: false,
      status: props.item.status,
      isShowStatus: true,
      isVisible: !isMobile,
    },
    {
      title: 'Starting time',
      content: `${props.scheduleStartDate} ${props.scheduleStartTime}`,
      isShowCopyIcon: false,
      status: null,
      isShowStatus: false,
      isVisible: isMobile,
    },
    {
      title: 'Room duration',
      content: hours
        ? `${hours}${t('hours')}${minutes ? ` ${minutes}${t('minutes')}` : ''}`
        : `${minutes}${t('minutes')}`,
      isShowCopyIcon: false,
      status: null,
      isShowStatus: false,
      isVisible: isMobile,
    },
    {
      title: 'Room Type',
      content: `${t(getSeatModeDisplay(props.item.basicRoomInfo.isSeatEnabled))}`,
      isShowCopyIcon: false,
      status: null,
      isShowStatus: false,
      isVisible: true,
    },
    {
      title: 'Room Password',
      content: `${roomInfo.value?.password}`,
      isShowCopyIcon: true,
      status: null,
      isShowStatus: false,
      isVisible: isShowPassword.value,
    },
    {
      title: 'Creator',
      content: props.item.basicRoomInfo.roomOwner,
      isShowCopyIcon: false,
      status: null,
      isShowStatus: false,
      isVisible: true,
    },
    {
      title: 'Participants',
      content: scheduleAttendeesInfo.value,
      isShowCopyIcon: false,
      status: null,
      isShowStatus: false,
      isVisible: true,
    },
    {
      title: 'Entry Time',
      content: `${props.scheduleStartDate}${props.scheduleStartTime}`,
      isShowCopyIcon: false,
      status: null,
      isShowStatus: false,
      isVisible:
        props.item.status === TUIConferenceStatus.kConferenceStatusNone,
    },
    {
      title: 'Duration of participation',
      content: getScheduleTime(durationTime.value),
      isShowCopyIcon: false,
      status: null,
      isShowStatus: false,
      isVisible:
        props.item.status === TUIConferenceStatus.kConferenceStatusNone,
    },
  ];
});

const scheduleInviteList = computed(() => [
  {
    title: 'Room Name',
    content: props.item.basicRoomInfo.roomName,
    isShowCopyIcon: false,
    isVisible: true,
  },
  {
    title: 'Room Type',
    content: `${t(getSeatModeDisplay(props.item.basicRoomInfo.isSeatEnabled))}`,
    isShowCopyIcon: false,
    isVisible: true,
  },
  {
    title: 'Room Time',
    content: !isMobile
      ? `${props.scheduleStartDate} ${props.scheduleStartTime} - ${props.scheduleEndDate} ${props.scheduleEndTime}`
      : `${props.scheduleStartDate} ${props.scheduleStartTime} - ${props.scheduleEndTime}`,
    isShowCopyIcon: false,
    isVisible: true,
  },
  {
    title: 'Room ID',
    content: props.item.basicRoomInfo.roomId,
    isShowCopyIcon: true,
    isVisible: true,
  },
  {
    title: 'Room Password',
    content: `${roomInfo.value?.password}`,
    isShowCopyIcon: true,
    isVisible: isShowPassword.value,
  },
  {
    title: 'Room Link',
    content: getUrlWithRoomId(props.item.basicRoomInfo.roomId),
    isShowCopyIcon: isMobile,
    isVisible: isRoomLinkVisible.value && roomLinkConfig.visible,
  },
]);

function getSeatModeDisplay(isSeatEnabled: boolean) {
  return isSeatEnabled ? 'On-stage Speaking Room' : 'Free Speech Room';
}

async function toggleClickMoreBtn() {
  emit('show-more', { roomId: props.item.basicRoomInfo.roomId });
  if (!showMoreControl.value) {
    await handleDropDownPosition();
  }
  showMoreControl.value = !showMoreControl.value;
}

function handleCloseOperate() {
  showMoreControl.value = false;
}

async function handleDropDownPosition() {
  await nextTick();
  const { top, bottom } = moreBtnRef.value?.getBoundingClientRect() || {};
  const { top: containerTop, bottom: containerBottom } = document
    .getElementById('scheduleRoomContainer')
    ?.getBoundingClientRect() as DOMRect;
  if (!containerBottom || !containerTop) {
    return;
  }
  const bottomSize = containerBottom - bottom;
  const topSize = top - containerTop;
  let dropDownContainerHeight = 0;
  if (!showMoreControl.value) {
    operateListRef.value.style =
      'display:block;position:absolute;z-index:-1000';
    await nextTick();
    dropDownContainerHeight = operateListRef.value.offsetHeight;
    operateListRef.value.style = '';
  } else {
    dropDownContainerHeight = operateListRef.value?.offsetHeight;
  }
  if (topSize < dropDownContainerHeight) {
    return;
  }
  if (bottomSize < dropDownContainerHeight) {
    dropdownClass.value = 'up';
  }
}

const invitePanelShow = ref(false);
const joinConference = () => {
  emit('join-conference', {
    roomId: props.item?.basicRoomInfo.roomId,
    roomParam: {
      isOpenCamera: false,
      isOpenMicrophone: true,
    },
  });
};
const toggleInvitePanelShow = () => {
  invitePanelShow.value = !invitePanelShow.value;
};
const showDetail = () => {
  if (!isMobile) return;
  emit('show-more', { roomId: props.item.basicRoomInfo.roomId });
  viewDetails.value.func();
};
function toggleInviteRoom() {
  if (showRoomDetail.value) {
    showRoomDetail.value = false;
  }
  showRoomInvite.value = !showRoomInvite.value;
}

function copyRoomIdAndRoomLink() {
  const invitationList = [
    `${props.item.basicRoomInfo.roomName}`,
    `${t('Room Type')}: ${t(getSeatModeDisplay(props.item.basicRoomInfo.isSeatEnabled))}`,
    `${t('Room ID')}: ${props.item.basicRoomInfo.roomId}`,
  ];
  if (isShowPassword.value) {
    invitationList.push(`${t('Room Password')}: ${roomInfo.value?.password}`);
  }
  if (isRoomLinkVisible.value && roomLinkConfig.visible) {
    invitationList.push(
      `${t('Room Link')}: ${getUrlWithRoomId(props.item.basicRoomInfo.roomId)}`
    );
  }
  const invitation = invitationList.join('\n');
  onCopy(invitation);
}

async function handleCancelSchedule() {
  await roomService.scheduleConferenceManager.cancelConference({
    roomId: props.item.basicRoomInfo.roomId,
  });
  showRoomCancel.value = false;
  showRoomDetail.value = false;
}

onMounted(async () => {
  try {
    const roomInfoParams = {
      roomId: props.item.basicRoomInfo.roomId,
      roomType: props.item.basicRoomInfo.roomType,
    };
    roomInfo.value = await roomService.fetchRoomInfo(roomInfoParams);
  } catch (error) {
    logger.error('fetch roomInfo failed:', error);
  }
});

return { __sfc: true,basicStore, userId, userName, isRoomLinkVisible, roomLinkConfig, t, showMoreControl, dropdownClass, moreBtnRef, operateListRef, modifyDialogVisible, emit, showRoomDetail, showRoomCancel, showRoomInvite, roomInfo, props, onCopy, statusMap, getStatusTextAndClass, isOwner, isShowPassword, viewDetails, modifyRoom, cancelRoom, moreControlList, scheduleAttendeesInfo, durationTime, getScheduleTime, scheduleRoomDetailList, scheduleInviteList, getSeatModeDisplay, toggleClickMoreBtn, handleCloseOperate, handleDropDownPosition, invitePanelShow, joinConference, toggleInvitePanelShow, showDetail, toggleInviteRoom, copyRoomIdAndRoomLink, handleCancelSchedule, TUIConferenceStatus, SvgIcon, Dialog, EllipsisIcon, ChevronRight, ArrowDown, LinkIcon, SuccessIcon, WarningIcon, TuiButton, ScheduleConferencePanel, isMobile, ConferenceDetail, InvitePanel, PanelContainer }
}

})